import { Button, Input } from "antd";
import queryClient from "api/queryClient.ts";
import { useGetIntegrationExportQuery, useUpdateIntegrationMutation } from "api/TenantSlug/Integration/hooks";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { PermissionEnum } from "types/enums";
import { FormId } from "types/models.ts";

import { formSchema, ModifyIntegrationFormData } from "./formSchema";

import IntegrationData = App.Data.IntegrationData;
import StoreIntegrationRequest = App.Http.Requests.Integration.StoreIntegrationRequest;

interface ApiToolGeneralFormProps {
    integration: IntegrationData;
    onClose: () => void;
}

const ModifyIntegrationFormContent: FC<ApiToolGeneralFormProps> = ({ integration, onClose }) => {
    const { t: tF } = usePaldockTranslation("forms.modify-integration");
    const { t: tC } = usePaldockTranslation("common");
    const generalFormUtils = useGeneralForm<ModifyIntegrationFormData>();
    const { control, handleSubmit, fieldNames } = generalFormUtils;
    const permissionsUtils = usePermission();

    const updateIntegrationMutation = useUpdateIntegrationMutation(integration.id ?? "");

    const onSubmitForm = (data: ModifyIntegrationFormData) => {
        const payload: StoreIntegrationRequest = JSON.parse(data.integration);

        updateIntegrationMutation.mutate(payload, {
            onSuccess: () => {
                toast.success(tF("messages.update_success"));
                queryClient.refetchQueries({ queryKey: ["getIntegration", integration.id] });
                queryClient.refetchQueries({ queryKey: ["getIntegrationExport", integration.id] });
                onClose();
            },
            onError: () => {
                toast.error(tF("messages.update_error"));
                control._resetDefaultValues;
            },
        });
    };

    return (
        <>
            <FormItem control={control} name={fieldNames.integration?.name || "integration"} label={tF("fields.integration.label")}>
                <Input.TextArea rows={4} placeholder={tF("fields.integration.placeholder")} />
            </FormItem>
            {/* <FormItem control={control} name={fieldNames.integration?.name || "integration"} label="Integration"> // todo edit via ace editor
                <CodeEditor id={fieldNames.integration?.name}>{control._defaultValues.integration}</CodeEditor>
            </FormItem>*/}
            {permissionsUtils.hasPermission(PermissionEnum.ToolIntegrationExportSave) && (
                <Button type="primary" onClick={handleSubmit(onSubmitForm)}>
                    {tC("save")}
                </Button>
            )}
        </>
    );
};

export const ModifyIntegrationForm: FC<ApiToolGeneralFormProps> = ({ integration, onClose }) => {
    const getIntegrationExportQuery = useGetIntegrationExportQuery(integration?.id || "");
    const data = getIntegrationExportQuery.data;

    if (!integration || !data) return null;
    return (
        <GeneralFormProvider<ModifyIntegrationFormData>
            schema={formSchema}
            id={FormId.ApiToolGeneralForm}
            defaultValues={{
                integration: JSON.stringify(data.integration),
            }}
        >
            <ModifyIntegrationFormContent integration={integration} onClose={onClose} />
        </GeneralFormProvider>
    );
};
